import {
  CustomRateReference,
  EntitlementType,
  ProductType,
  SaveCustomPriceInput,
  SubscriptionDetailsBreakdown,
  Tier,
} from '@graphql';

export type SaveCustomPriceInputWithName = SaveCustomPriceInput & {
  name: string;
};

/**
 * Builds the initial pricing values for a given organization tier and its add-ons.
 *
 * @param tiers Full list of platform tiers (from listTiers query)
 * @param currentTierId The organization's current tier ID
 * @param customPrices Optional list of custom prices (from backend)
 * @returns Ordered array of PricingItem objects: [tier, ...addOns]
 */
export function buildInitialPricingValues(
  currentTierId: string | undefined,
  tiers: Tier[] | undefined,
  customPrices: CustomRateReference[] | undefined,
  breakdown: SubscriptionDetailsBreakdown[] | undefined,
): SaveCustomPriceInputWithName[] {
  if (!tiers || !currentTierId) return [];

  // Find the current tier in the list
  const currentTier = tiers.find((t) => t.id === currentTierId);
  if (!currentTier) return [];

  const items: SaveCustomPriceInputWithName[] = [];

  // Tier item always comes first
  const tierCustom = customPrices?.find(
    (c) => c.productType === ProductType.TIER && c.tierId === currentTierId,
  );

  items.push({
    id: tierCustom?.id ?? undefined,
    name: currentTier.name,
    tierId: currentTier.id,
    featureId: undefined,
    amount: tierCustom?.amount
      ?? breakdown?.find((b) => b.type === ProductType.TIER)?.amount
      ?? 0,
  });

  // Collect add-ons from entitlements of type "add-on"
  const addOns = currentTier.entitlements?.filter((e) => e.type === EntitlementType.ADD_ON) ?? [];

  addOns.forEach((entitlement) => {
    const { feature } = entitlement;
    if (!feature) return;

    // Look for a custom price for this add-on (by featureId)
    const custom = customPrices?.find(
      (c) => c.productType === ProductType.FEATURE && c.featureId === feature.id,
    );

    items.push({
      id: custom?.id ?? undefined,
      name: feature.name,
      tierId: undefined,
      featureId: feature.id,
      amount: custom?.amount
        ?? breakdown?.find((b) => b.type === ProductType.FEATURE && b.name === feature.name)?.amount
        ?? 0,
    });
  });

  return items;
}

/**
 * Cleans the prices to save by filtering out prices that are the same as the initial prices and filtering out prices that are 0.
 * @param prices - the prices to save
 * @param initialPrices - the initial prices
 * @returns 
 */
export function buildSaveCustomPricesInput(
  prices: SaveCustomPriceInputWithName[],
  initialPrices: SaveCustomPriceInputWithName[],
): SaveCustomPriceInput[] {

  // we must filter out prices that are the same as the initial prices
  const changedPrices = prices.filter((price) => {
    const initialPrice = initialPrices.find((p) => p.id === price.id);
    return price.amount !== initialPrice?.amount;
  });

  const mapped = changedPrices.map((price) => ({
    id: price.id,
    tierId: price.tierId,
    featureId: price.featureId,
    amount: price.amount,
  }));

  return mapped;
}
